// utils/bankAccountModalStatus.js
//
// Frontend mirror of server_elakay/elakay_helpers/bankAccountModalStatus.js
// -- front_elakay and server_elakay are separate git repos/build trees (no
// shared module boundary), so the constants/fallback logic are kept here in
// sync by hand rather than duplicated ad hoc inside BankAccountRequiredModal.jsx
// or AnimationSettings.jsx (same convention as utils/sellCenterStatus.js).
//
// Role keys/normalization are NOT redefined here -- reused directly from
// utils/sellCenterStatus.js (same 6 roles, same getCurrentElakayRole()-based
// normalization) per spec: never invent a second role list when one already
// exists.
import { SELL_CENTER_ROLES, normalizeSellCenterRole } from "./sellCenterStatus";

// Page keys are real, distinguishable ROUTES only (see Routes.js). "Sell
// Center", "Add Product" (Seller) and "My Products" (Seller) are internal
// view-states of the single /myShop route (SellingHubButtons.jsx /
// addProduct.jsx rendered conditionally inside userProduct.js, never in the
// URL) -- they collapse into the single `myShop` key rather than fabricating
// config entries a route resolver could never actually distinguish.
export const BANK_ACCOUNT_MODAL_PAGE_KEYS = [
  "myShop",
  "dropshippingIn",
  "dropshippingOut",
  "providerProducts",
  "orders",
  "emailMarketing",
  "adminAssociate",
  "wallet",
];

// Preserves today's observed behavior exactly: Seller() cascades up through
// Provider/Associate/Investment/Adminstrator/Family, so the modal already
// shows for every one of those roles on these 3 pages -- default every role
// to true there. The 5 newly-configurable pages default to false (the modal
// never appeared there before this feature; a Family admin opts a role in
// explicitly via Animation Settings).
export const BANK_ACCOUNT_MODAL_PAGE_DEFAULTS = {
  myShop: true,
  adminAssociate: true,
  wallet: true,
  dropshippingIn: false,
  dropshippingOut: false,
  providerProducts: false,
  orders: false,
  emailMarketing: false,
};

// Display metadata for the Animation Settings admin matrix -- same shape as
// utils/sellCenterStatus.js's SELL_CENTER_FEATURES.
export const BANK_ACCOUNT_MODAL_PAGES = [
  { key: "myShop", labelKey: "bankAccountModalPageMyShop", labelFallback: "My Shop (Sell Center / Add Product)" },
  { key: "dropshippingIn", labelKey: "bankAccountModalPageDropshippingIn", labelFallback: "Dropshipping In" },
  { key: "dropshippingOut", labelKey: "bankAccountModalPageDropshippingOut", labelFallback: "Dropshipping Out" },
  { key: "providerProducts", labelKey: "bankAccountModalPageProviderProducts", labelFallback: "Provider Products" },
  { key: "orders", labelKey: "bankAccountModalPageOrders", labelFallback: "Orders" },
  { key: "emailMarketing", labelKey: "bankAccountModalPageEmailMarketing", labelFallback: "Email Marketing" },
  { key: "adminAssociate", labelKey: "bankAccountModalPageAdminAssociate", labelFallback: "Admin / Associate" },
  { key: "wallet", labelKey: "bankAccountModalPageWallet", labelFallback: "Wallet" },
];

// A route -> logical pageKey resolver, on the exact model of Routes.js's own
// elakayRouteTitleRules -- regex-based so dynamic segments (/provider/
// products/:id/edit, /seller/provider-products/:id, .../editor/:id) resolve
// correctly instead of requiring an exact string match per route.
const BANK_ACCOUNT_MODAL_ROUTE_RULES = [
  { pageKey: "myShop", pattern: /^\/myshop$/ },
  { pageKey: "dropshippingIn", pattern: /^\/seller\/(dropshipping-in|providers|provider-products)(\/.*)?$/ },
  { pageKey: "dropshippingOut", pattern: /^\/dropshipping(\/.*)?$/ },
  { pageKey: "providerProducts", pattern: /^\/provider\/products(\/.*)?$/ },
  { pageKey: "orders", pattern: /^\/c\/orders(\/.*)?$/ },
  { pageKey: "emailMarketing", pattern: /^\/elakay\/email-marketing-(dashboard|editor)(\/.*)?$/ },
  { pageKey: "adminAssociate", pattern: /^\/admin_associat$/ },
  { pageKey: "wallet", pattern: /^\/wallet$/ },
];

export const resolveBankAccountModalPageKey = (pathname) => {
  const clean = String(pathname || "").toLowerCase().replace(/\/+$/, "") || "/";
  return BANK_ACCOUNT_MODAL_ROUTE_RULES.find(({ pattern }) => pattern.test(clean))?.pageKey || null;
};

// The one function every bank-account-modal trigger reads through -- never
// duplicate this chain inline. `allSettings` is infoSource.allSettings
// (already loaded once for the whole app -- this never triggers its own
// fetch). `role` is getCurrentElakayRole(infoSource)'s output (or already-
// normalized); mirrors the backend's getBankAccountModalVisibility exactly.
export const getBankAccountModalVisibility = (allSettings, pageKey, role) => {
  if (allSettings?.bankAccountModal?.enabled === false) return false;

  const page = allSettings?.bankAccountModal?.pages?.[pageKey];
  if (!page) return false;
  if (page.enabled === false) return false;

  const normalizedRole = normalizeSellCenterRole(role);
  if (!normalizedRole) return false;

  return Boolean(page.statusByRole?.[normalizedRole]);
};

export { SELL_CENTER_ROLES as BANK_ACCOUNT_MODAL_ROLES };
